import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Plus, Trash2, AlertCircle, Loader2 } from "lucide-react";
import {
  useAttractionAdmin,
  useUpdateAttraction,
  type AttractionTranslation,
} from "../../../services/attractionService";
import { privateRequest } from "../../../utils/api";
import { useSelector } from "react-redux";
import type { RootState } from "../../../store";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import ImageUpload from "../../../components/common/ImageUpload";
import { ACCEPTABLE_LANGUAGES } from "../../../constants/languages";

interface AttractionFormData {
  category?: string;
  image?: string;
  gallery?: string[];
  location?: string;
  location_image?: string;
  opening_hours?: string;
  entrance_fee?: string;
  accessibility?: string;
  nearby_attractions?: Array<{
    id?: string;
    title?: string;
    description?: string;
    image?: string;
  }>;
  featured?: boolean;
  status?: string;
  latitude?: number;
  longitude?: number;
  translations: AttractionTranslation[];
}

const AttractionEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { currentLanguage } = useSelector((state: RootState) => state.language);
  const navigate = useNavigate();

  const {
    data: attraction,
    isLoading: isLoadingAttraction,
    isError: isErrorAttraction,
  } = useAttractionAdmin(id ? parseInt(id) : 0, !!id);
  const updateAttractionMutation = useUpdateAttraction();

  const [formData, setFormData] = useState<AttractionFormData>({
    category: "",
    image: "",
    gallery: [],
    location: "",
    location_image: "",
    opening_hours: "",
    entrance_fee: "",
    accessibility: "",
    nearby_attractions: [],
    featured: false,
    status: "draft",
    latitude: undefined,
    longitude: undefined,
    translations: ACCEPTABLE_LANGUAGES.map((lang) => ({
      locale: lang,
      name: "",
      description: "",
      about: "",
      slug: "",
    })),
  });

  const [currentNearbyTitle, setCurrentNearbyTitle] = useState("");
  const [currentNearbyDescription, setCurrentNearbyDescription] = useState("");
  const [currentNearbyImage, setCurrentNearbyImage] = useState("");
  const [validationError, setValidationError] = useState("");

  // Populate form when attraction data is loaded
  useEffect(() => {
    if (attraction) {
      setFormData({
        category: attraction.category || "",
        image: attraction.image || "",
        gallery: attraction.gallery || attraction.images || [],
        location: attraction.location || "",
        location_image: attraction.locationImage || "",
        opening_hours: attraction.practicalInfo?.openingHours || "",
        entrance_fee: attraction.practicalInfo?.entranceFee || "",
        accessibility: attraction.practicalInfo?.accessibility || "",
        nearby_attractions: (attraction.nearbyAttractions || []).map(
          (nearby: {
            id?: string;
            title?: string;
            description?: string;
            image?: string;
          }) => ({
            ...nearby,
            id: nearby.id || crypto.randomUUID(),
          })
        ),
        featured: attraction.featured || false,
        status: attraction.status || "draft",
        latitude: attraction.latitude,
        longitude: attraction.longitude,
        translations: ACCEPTABLE_LANGUAGES.map((lang) => {
          const langTranslation = attraction.translations?.find(
            (t: AttractionTranslation) => t.locale === lang
          );
          return {
            locale: lang,
            name: langTranslation?.name || attraction.title || "",
            description:
              langTranslation?.description || attraction.description || "",
            about: langTranslation?.about || attraction.about || "",
            slug: langTranslation?.slug || "",
          };
        }),
      });
    }
  }, [attraction]);

  const handleInputChange = (
    field: keyof AttractionFormData,
    value: string | number | boolean | string[] | object | undefined
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleTranslationChange = (
    locale: string,
    field: keyof AttractionTranslation,
    value: string
  ) => {
    setFormData((prev) => ({
      ...prev,
      translations: prev.translations.map((translation) =>
        translation.locale === locale
          ? { ...translation, [field]: value }
          : translation
      ),
    }));
  };

  const addNearbyAttraction = () => {
    if (currentNearbyTitle.trim()) {
      setFormData((prev) => ({
        ...prev,
        nearby_attractions: [
          ...(prev.nearby_attractions || []),
          {
            id: crypto.randomUUID(),
            title: currentNearbyTitle.trim(),
            description: currentNearbyDescription.trim() || undefined,
            image: currentNearbyImage.trim() || undefined,
          },
        ],
      }));
      setCurrentNearbyTitle("");
      setCurrentNearbyDescription("");
      setCurrentNearbyImage("");
    }
  };

  const removeNearbyAttraction = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      nearby_attractions:
        prev.nearby_attractions?.filter((_, i) => i !== index) || [],
    }));
  };

  const handleImageUpload = useCallback(
    async (files: File[]): Promise<string[]> => {
      const file = files[0];
      if (!file) return [];

      try {
        const formData = new FormData();
        formData.append("file", file);
        const res = await privateRequest.post("/upload", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const data = res.data?.data || {};
        if (Array.isArray(data.urls) && data.urls.length > 0) return data.urls;
        if (data.url) return [data.url];
        return [];
      } catch (err) {
        console.error("Image upload failed:", err);
        throw err;
      }
    },
    []
  );

  const handleImageChange = useCallback((url: string | string[]) => {
    const imageUrl = Array.isArray(url) ? url[0] || "" : url || "";
    setFormData((prev) => ({ ...prev, image: imageUrl }));
  }, []);

  const handleLocationImageUpload = useCallback(
    async (files: File[]): Promise<string[]> => {
      const file = files[0];
      if (!file) return [];

      try {
        const formData = new FormData();
        formData.append("file", file);
        const res = await privateRequest.post("/upload", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const data = res.data?.data || {};
        if (Array.isArray(data.urls) && data.urls.length > 0) return data.urls;
        if (data.url) return [data.url];
        return [];
      } catch (err) {
        console.error("Location image upload failed:", err);
        throw err;
      }
    },
    []
  );

  const handleLocationImageChange = useCallback((url: string | string[]) => {
    const imageUrl = Array.isArray(url) ? url[0] || "" : url || "";
    setFormData((prev) => ({ ...prev, location_image: imageUrl }));
  }, []);

  const handleGalleryUpload = useCallback(
    async (files: File[]): Promise<string[]> => {
      try {
        // Upload each file individually, same as main image and location image
        const uploadPromises = files.map(async (file) => {
          const formData = new FormData();
          formData.append("file", file);
          const res = await privateRequest.post("/upload", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          const data = res.data?.data || {};
          // Return the first URL from the response
          if (Array.isArray(data.urls) && data.urls.length > 0) return data.urls[0];
          if (data.url) return data.url;
          return null;
        });

        const urls = await Promise.all(uploadPromises);
        // Filter out any null values
        return urls.filter((url): url is string => url !== null);
      } catch (error) {
        console.error("Error uploading gallery:", error);
        throw error;
      }
    },
    []
  );

  const handleGalleryChange = useCallback((urls: string | string[]) => {
    const urlArray = Array.isArray(urls) ? urls : [urls].filter(Boolean);
    setFormData((prev) => ({ ...prev, gallery: urlArray }));
  }, []);

  const generateSlug = (text: string): string => {
    return text
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "")
      .replace(/[\s_-]+/g, "-")
      .replace(/^-+|-+$/g, "");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError("");

    if (!id) {
      setValidationError("Attraction ID is required");
      return;
    }

    // Validate at least one translation has name
    const hasValidTranslation = formData.translations.some(
      (t) => t.name && t.name.trim()
    );
    if (!hasValidTranslation) {
      setValidationError("At least one translation with name is required");
      return;
    }

    // Check for blob: URLs in gallery or images
    const hasBlobUrls = [
      formData.image,
      formData.location_image,
      ...(formData.gallery || []),
    ].some((url) => url && typeof url === "string" && url.startsWith("blob:"));

    if (hasBlobUrls) {
      setValidationError(
        "Some images are still uploading. Please wait or try re-uploading."
      );
      return;
    }

    // Generate slugs for translations that don't have them
    const translationsWithSlugs = formData.translations.map((translation) => ({
      ...translation,
      slug: translation.slug || generateSlug(translation.name || ""),
    }));

    // Final safety check: Filter out any blob: URLs that might have leaked
    const cleanImage =
      formData.image && formData.image.startsWith("blob:")
        ? ""
        : formData.image;
    const cleanLocationImage =
      formData.location_image && formData.location_image.startsWith("blob:")
        ? ""
        : formData.location_image;
    const cleanGallery = (formData.gallery || []).filter(
      (url) => url && typeof url === "string" && !url.startsWith("blob:")
    );

    try {
      await updateAttractionMutation.mutateAsync({
        id: parseInt(id),
        attractionData: {
          category: formData.category || undefined,
          image: cleanImage || undefined,
          gallery: cleanGallery.length > 0 ? cleanGallery : undefined,
          location: formData.location || undefined,
          location_image: cleanLocationImage || undefined,
          opening_hours: formData.opening_hours || undefined,
          entrance_fee: formData.entrance_fee || undefined,
          accessibility: formData.accessibility || undefined,
          nearby_attractions:
            formData.nearby_attractions &&
              formData.nearby_attractions.length > 0
              ? formData.nearby_attractions
              : undefined,
          featured: formData.featured || false,
          status: formData.status || "draft",
          latitude: formData.latitude,
          longitude: formData.longitude,
          translations: translationsWithSlugs.filter(
            (t) => t.name && t.name.trim()
          ),
        },
      });
      navigate(`/${currentLanguage}/admin/attractions`);
    } catch (error: unknown) {
      console.error("Failed to update attraction:", error);
      if (error && typeof error === "object" && "response" in error) {
        const axiosError = error as {
          response?: { data?: { errors?: Record<string, string[]> } };
        };
        if (axiosError.response?.data?.errors) {
          const errors = axiosError.response.data.errors;
          const firstError = Object.values(errors).flat()[0];
          setValidationError(firstError || "Failed to update attraction");
        } else {
          setValidationError("Failed to update attraction");
        }
      } else if (error instanceof Error) {
        setValidationError(error.message || "Failed to update attraction");
      } else {
        setValidationError("Failed to update attraction");
      }
    }
  };

  if (isLoadingAttraction) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-6">
        <div className="max-w-6xl mx-auto flex justify-center items-center py-20">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">
              {t("common.loading", "Loading...")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (isErrorAttraction || !attraction) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-red-600 dark:text-red-400 flex-1">
              {t(
                "admin.attractions.errorLoading",
                "Error loading attraction. Please try again."
              )}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-4"
        >
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/${currentLanguage}/admin/attractions`)}
            className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t("common.back", "Back")}
          </Button>
          <div>
            <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600">
              {t("admin.attractions.editAttraction", "Edit Attraction")}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {t(
                "admin.attractions.editSubtitle",
                "Update attraction information"
              )}
            </p>
          </div>
        </motion.div>

        {/* Validation Error */}
        {validationError && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg"
          >
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-red-600 dark:text-red-400 flex-1">
              {validationError}
            </p>
          </motion.div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <Card className="bg-white dark:bg-gray-800 border-0 shadow-lg">
            <CardHeader>
              <CardTitle>
                {t("admin.attractions.basicInfo", "Basic Information")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="category">
                    {t("admin.attractions.category", "Category")}
                  </Label>
                  <Input
                    id="category"
                    value={formData.category}
                    onChange={(e) =>
                      handleInputChange("category", e.target.value)
                    }
                    placeholder={t(
                      "admin.attractions.categoryPlaceholder",
                      "e.g., Historical Sites, Natural Wonders"
                    )}
                  />
                </div>
                <div>
                  <Label htmlFor="location">
                    {t("admin.attractions.location", "Location")}
                  </Label>
                  <Input
                    id="location"
                    value={formData.location}
                    onChange={(e) =>
                      handleInputChange("location", e.target.value)
                    }
                    placeholder={t(
                      "admin.attractions.locationPlaceholder",
                      "Attraction address"
                    )}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="latitude">
                    {t("admin.attractions.latitude", "Latitude")}
                  </Label>
                  <Input
                    id="latitude"
                    type="number"
                    step="any"
                    value={formData.latitude || ""}
                    onChange={(e) =>
                      handleInputChange(
                        "latitude",
                        e.target.value ? parseFloat(e.target.value) : undefined
                      )
                    }
                    placeholder="e.g., 4.0511"
                  />
                </div>
                <div>
                  <Label htmlFor="longitude">
                    {t("admin.attractions.longitude", "Longitude")}
                  </Label>
                  <Input
                    id="longitude"
                    type="number"
                    step="any"
                    value={formData.longitude || ""}
                    onChange={(e) =>
                      handleInputChange(
                        "longitude",
                        e.target.value ? parseFloat(e.target.value) : undefined
                      )
                    }
                    placeholder="e.g., 9.7679"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Images */}
          <Card className="bg-white dark:bg-gray-800 border-0 shadow-lg">
            <CardHeader>
              <CardTitle>{t("admin.attractions.images", "Images")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>{t("admin.attractions.mainImage", "Main Image")}</Label>
                <ImageUpload
                  value={formData.image || ""}
                  onChange={handleImageChange}
                  onUpload={handleImageUpload}
                  maxFiles={1}
                />
              </div>
              <div>
                <Label>{t("admin.attractions.gallery", "Gallery")}</Label>
                <ImageUpload
                  value={formData.gallery || []}
                  onChange={handleGalleryChange}
                  onUpload={handleGalleryUpload}
                  multiple
                  maxFiles={10}
                />
              </div>
              <div>
                <Label>
                  {t("admin.attractions.locationImage", "Location Image")}
                </Label>
                <ImageUpload
                  value={formData.location_image || ""}
                  onChange={handleLocationImageChange}
                  onUpload={handleLocationImageUpload}
                  maxFiles={1}
                />
              </div>
            </CardContent>
          </Card>

          {/* Practical Information */}
          <Card className="bg-white dark:bg-gray-800 border-0 shadow-lg">
            <CardHeader>
              <CardTitle>
                {t("admin.attractions.practicalInfo", "Practical Information")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="opening_hours">
                  {t("admin.attractions.openingHours", "Opening Hours")}
                </Label>
                <Input
                  id="opening_hours"
                  value={formData.opening_hours}
                  onChange={(e) =>
                    handleInputChange("opening_hours", e.target.value)
                  }
                  placeholder={t(
                    "admin.attractions.openingHoursPlaceholder",
                    "e.g., Daily, 9 AM - 6 PM"
                  )}
                />
              </div>
              <div>
                <Label htmlFor="entrance_fee">
                  {t("admin.attractions.entranceFee", "Entrance Fee")}
                </Label>
                <Input
                  id="entrance_fee"
                  value={formData.entrance_fee}
                  onChange={(e) =>
                    handleInputChange("entrance_fee", e.target.value)
                  }
                  placeholder={t(
                    "admin.attractions.entranceFeePlaceholder",
                    "e.g., Free, $10"
                  )}
                />
              </div>
              <div>
                <Label htmlFor="accessibility">
                  {t("admin.attractions.accessibility", "Accessibility")}
                </Label>
                <textarea
                  id="accessibility"
                  value={formData.accessibility}
                  onChange={(e) =>
                    handleInputChange("accessibility", e.target.value)
                  }
                  placeholder={t(
                    "admin.attractions.accessibilityPlaceholder",
                    "e.g., Wheelchair accessible paths available"
                  )}
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          {/* Nearby Attractions */}
          <Card className="bg-white dark:bg-gray-800 border-0 shadow-lg">
            <CardHeader>
              <CardTitle>
                {t("admin.attractions.nearbyAttractions", "Nearby Attractions")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  value={currentNearbyTitle}
                  onChange={(e) => setCurrentNearbyTitle(e.target.value)}
                  placeholder={t(
                    "admin.attractions.nearbyTitlePlaceholder",
                    "Attraction title"
                  )}
                  className="flex-1"
                />
                <Input
                  value={currentNearbyDescription}
                  onChange={(e) => setCurrentNearbyDescription(e.target.value)}
                  placeholder={t(
                    "admin.attractions.nearbyDescriptionPlaceholder",
                    "Description"
                  )}
                  className="flex-1"
                />
                <Input
                  value={currentNearbyImage}
                  onChange={(e) => setCurrentNearbyImage(e.target.value)}
                  placeholder={t(
                    "admin.attractions.nearbyImagePlaceholder",
                    "Image URL"
                  )}
                  className="flex-1"
                />
                <Button type="button" onClick={addNearbyAttraction}>
                  <Plus className="w-4 h-4 mr-2" />
                  {t("common.add", "Add")}
                </Button>
              </div>
              <div className="space-y-2">
                {formData.nearby_attractions?.map((nearby, index) => (
                  <div
                    key={nearby.id || index}
                    className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded"
                  >
                    <div className="flex-1">
                      <span className="font-medium">{nearby.title}</span>
                      {nearby.description && (
                        <span className="text-sm text-gray-600 dark:text-gray-400 ml-2">
                          - {nearby.description}
                        </span>
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeNearbyAttraction(index)}
                      className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Status & Featured */}
          <Card className="bg-white dark:bg-gray-800 border-0 shadow-lg">
            <CardHeader>
              <CardTitle>
                {t("admin.attractions.statusFeatured", "Status & Featured")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="status">
                  {t("admin.attractions.status", "Status")}
                </Label>
                <select
                  id="status"
                  value={formData.status}
                  onChange={(e) => handleInputChange("status", e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                >
                  <option value="draft">
                    {t("admin.attractions.draft", "Draft")}
                  </option>
                  <option value="published">
                    {t("admin.attractions.published", "Published")}
                  </option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="featured"
                  checked={formData.featured}
                  onChange={(e) =>
                    handleInputChange("featured", e.target.checked)
                  }
                  className="w-4 h-4"
                />
                <Label htmlFor="featured" className="cursor-pointer">
                  {t("admin.attractions.featured", "Featured")}
                </Label>
              </div>
            </CardContent>
          </Card>

          {/* Translations */}
          <Card className="bg-white dark:bg-gray-800 border-0 shadow-lg">
            <CardHeader>
              <CardTitle>
                {t("admin.attractions.translations", "Translations")}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-6">
              {ACCEPTABLE_LANGUAGES.map((lang) => {
                const translation = formData.translations.find(
                  (t) => t.locale === lang
                );
                return (
                  <div
                    key={lang}
                    className="space-y-4 p-4 border border-gray-200 dark:border-gray-700 rounded-lg"
                  >
                    <h3 className="font-semibold text-gray-900 dark:text-white uppercase">
                      {lang}
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor={`name-${lang}`}>
                          {t("admin.attractions.name", "Name")} *
                        </Label>
                        <Input
                          id={`name-${lang}`}
                          value={translation?.name || ""}
                          onChange={(e) => {
                            handleTranslationChange(
                              lang,
                              "name",
                              e.target.value
                            );
                            // Auto-generate slug if empty
                            if (!translation?.slug) {
                              handleTranslationChange(
                                lang,
                                "slug",
                                generateSlug(e.target.value)
                              );
                            }
                          }}
                          placeholder={t(
                            "admin.attractions.namePlaceholder",
                            "Attraction name"
                          )}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor={`slug-${lang}`}>
                          {t("admin.attractions.slug", "Slug")} *
                        </Label>
                        <Input
                          id={`slug-${lang}`}
                          value={translation?.slug || ""}
                          onChange={(e) =>
                            handleTranslationChange(
                              lang,
                              "slug",
                              e.target.value
                            )
                          }
                          placeholder={t(
                            "admin.attractions.slugPlaceholder",
                            "attraction-slug"
                          )}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor={`description-${lang}`}>
                          {t("admin.attractions.description", "Description")}
                        </Label>
                        <textarea
                          id={`description-${lang}`}
                          value={translation?.description || ""}
                          onChange={(e) =>
                            handleTranslationChange(
                              lang,
                              "description",
                              e.target.value
                            )
                          }
                          placeholder={t(
                            "admin.attractions.descriptionPlaceholder",
                            "Short description"
                          )}
                          className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                          rows={3}
                        />
                      </div>
                      <div>
                        <Label htmlFor={`about-${lang}`}>
                          {t("admin.attractions.about", "About")}
                        </Label>
                        <textarea
                          id={`about-${lang}`}
                          value={translation?.about || ""}
                          onChange={(e) =>
                            handleTranslationChange(
                              lang,
                              "about",
                              e.target.value
                            )
                          }
                          placeholder={t(
                            "admin.attractions.aboutPlaceholder",
                            "Detailed about information"
                          )}
                          className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                          rows={5}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {/* Submit Button */}
          <div className="flex justify-end gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(`/${currentLanguage}/admin/attractions`)}
            >
              {t("common.cancel", "Cancel")}
            </Button>
            <Button
              type="submit"
              disabled={updateAttractionMutation.isPending}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
            >
              {updateAttractionMutation.isPending
                ? t("common.updating", "Updating...")
                : t("admin.attractions.update", "Update Attraction")}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AttractionEdit;
